/**
 * Transforms the Belle Journée API response to match the frontend menu schema
 */

// Map location names to store IDs
export const STORE_ID_MAP = {
  'Princeton': 1,
  'Fort Lee': 2,
  'Palisades Park': 3
};

// Reverse map for store ID to location name
export const LOCATION_MAP = {
  1: 'Princeton',
  2: 'Fort Lee',
  3: 'Palisades Park'
};

/**
 * Transform API response to frontend menu format
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} - Transformed menu data { menuItems, categories }
 */
export const transformMenuData = (apiResponse) => {
  if (!apiResponse || apiResponse.status !== 'success') {
    throw new Error('Invalid API response');
  }

  const { data } = apiResponse;

  if (!data || !data.categories) {
    throw new Error('No menu data available');
  }

  // Extract all items from all categories and flatten them
  const menuItems = [];
  const categoryNames = ['All']; // Start with 'All' filter

  data.categories.forEach((category) => {
    // Add category name to list
    categoryNames.push(category.title);

    // Transform each item in this category
    if (category.items && category.items.length > 0) {
      category.items.forEach((item) => {
        const transformedItem = {
          id: item.id,
          name: item.title,
          description: item.description || '', // Leave blank if no description
          price: parseFloat(item.price),
          tax: parseFloat(item.tax),
          category: category.title,
          image: item.image || '/logo.png', // Fallback to logo.png if no image
          sort: item.sort,
          modifier_groups: item.modifier_groups || []
        };
        menuItems.push(transformedItem);
      });
    }
  });

  // Sort items by their sort order
  menuItems.sort((a, b) => a.sort - b.sort);

  return {
    menuItems,
    categories: categoryNames
  };
};

/**
 * Fetch menu from API for a specific location
 * @param {string} location - Location name (Princeton, Fort Lee, or Palisades Park)
 * @returns {Promise<Object>} - Menu data
 */
export const fetchMenuForLocation = async (location) => {
  const storeId = STORE_ID_MAP[location];

  if (!storeId) {
    throw new Error(`Invalid location: ${location}`);
  }

  const apiUrl = `https://online.bellejournee.com/api/index.php?request=getmenu&store_id=${storeId}`;

  try {
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return transformMenuData(data);
  } catch (error) {
    console.error('Error fetching menu:', error);
    throw error;
  }
};

/**
 * Calculate cart totals using the API
 * @param {Array} cartItems - Array of cart items with id and quantity
 * @param {string} location - Location name (Princeton, Fort Lee, or Palisades Park)
 * @returns {Promise<Object>} - Cart calculation response
 */
export const calculateCart = async (cartItems, location) => {
  const storeId = STORE_ID_MAP[location];

  if (!storeId) {
    throw new Error(`Invalid location: ${location}`);
  }

  // Transform cart items to API format: [{ item: id, qty: quantity }]
  const cart = cartItems.map(item => ({
    item: item.id,
    qty: item.quantity
  }));

  const formData = new FormData();
  formData.append('store_id', storeId);
  formData.append('cart', JSON.stringify(cart));

  const apiUrl = 'https://online.bellejournee.com/api/index.php?request=cartcalc';

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error calculating cart:', error);
    throw error;
  }
};

/**
 * Convert pickup date from "November 26" format to "2025-11-26" format
 * @param {string} dateString - Date string like "November 26"
 * @returns {string} - Formatted date string like "2025-11-26"
 */
const formatPickupDate = (dateString) => {
  const monthMap = {
    'January': '01', 'February': '02', 'March': '03', 'April': '04',
    'May': '05', 'June': '06', 'July': '07', 'August': '08',
    'September': '09', 'October': '10', 'November': '11', 'December': '12'
  };

  const [month, day] = dateString.split(' ');
  const monthNum = monthMap[month];
  const dayNum = day.padStart(2, '0');

  // Use 2025 as the year for the holiday event
  return `2025-${monthNum}-${dayNum}`;
};

/**
 * Process payment and submit order
 * @param {Object} orderData - Complete order data
 * @param {string} orderData.location - Location name
 * @param {Array} orderData.cartItems - Cart items
 * @param {Object} orderData.formData - Form data with customer, billing, payment info
 * @returns {Promise<Object>} - Payment processing response
 */
export const processPayment = async (orderData) => {
  const { location, cartItems, formData } = orderData;
  const storeId = STORE_ID_MAP[location];

  if (!storeId) {
    throw new Error(`Invalid location: ${location}`);
  }

  // Transform cart items to API format: [{ item: id, qty: quantity }]
  const cart = cartItems.map(item => ({
    item: item.id,
    qty: item.quantity
  }));

  // Build the payload according to API specification
  const payload = {
    store_id: storeId,
    customer: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone
    },
    billing: {
      address: formData.billingAddress,
      city: formData.billingCity,
      state: formData.billingState,
      zip: formData.billingZipcode,
      country: 'US'
    },
    payment: {
      cardNumber: formData.cardNumber.replace(/\s/g, ''), // Remove spaces
      expDate: formData.expiryDate, // Already in YYYY-MM format
      cvv: formData.cvv
    },
    pickup_date: formatPickupDate(formData.pickupDate),
    pickup_time: formData.pickupTime,
    special_instructions: formData.specialInstructions || '',
    cart: cart
  };

  const apiUrl = 'https://online.bellejournee.com/api/index.php?request=processPayment';

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};
